import type { PortfolioAllocationGridSlots } from '@components/PortfolioAllocationGrid';
import {
  TableCell,
  TableHeadCell,
} from '@ovComponents/2-component/table/table';
import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import type {
  AccountAllocationComparison,
  AccountAllocationComparisonCell,
} from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/reviewAccountAllocationsModel';
import { formatPercentValue } from '@utils/moneyFormatting';
import type { TFunction } from 'i18next';
import {
  ACCOUNT_ALLOCATION_CELL_SX,
  type BuildAccountAllocationFrameSlotsArgs,
  buildAccountAllocationFrameSlots,
  EN_DASH,
  TESTID_ACCOUNT_ALLOCATION_CELL,
  TESTID_ACCOUNT_ALLOCATION_CURRENT_CELL,
  TESTID_ACCOUNT_ALLOCATION_CURRENT_TOTAL_CELL,
  TESTID_ACCOUNT_ALLOCATION_TOTAL_CELL,
} from './buildAccountAllocationSlots';

// Proposed and Current sit side by side under each account.
export const COMPARISON_COLUMNS_PER_ACCOUNT = 2;

const percentOrDash = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined
    ? EN_DASH
    : formatPercentValue(fraction);

// The proposed value is what gets signed. A changed one is bold, and says
// what it changes from to a screen reader, since the Current column beside it
// is a separate cell.
const proposedCell = (
  cell: AccountAllocationComparisonCell | undefined,
  testId: string,
  t: TFunction,
) => (
  <TableCell
    sx={ACCOUNT_ALLOCATION_CELL_SX}
    right
    bold={cell?.changed}
    data-testid={testId}
    aria-label={
      cell?.changed
        ? (t('components:accountAllocationGrid.comparison.changedFrom', {
            current: percentOrDash(cell.current),
            proposed: percentOrDash(cell.proposed),
          }) ?? undefined)
        : undefined
    }
  >
    {percentOrDash(cell?.proposed)}
  </TableCell>
);

const textCell = (text: string, testId?: string) => (
  <TableCell sx={ACCOUNT_ALLOCATION_CELL_SX} right data-testid={testId}>
    {text}
  </TableCell>
);

export type BuildAccountAllocationComparisonSlotsArgs = Omit<
  BuildAccountAllocationFrameSlotsArgs,
  'accountColumnSpan'
> & {
  comparison: AccountAllocationComparison;
};

// Every number comes from the comparison, which reads the staged matrix: the
// grid shows exactly what will be signed and applied, and does no arithmetic.
export const buildAccountAllocationComparisonSlots = ({
  comparison,
  ...frameArgs
}: BuildAccountAllocationComparisonSlotsArgs): PortfolioAllocationGridSlots => {
  const { report, t } = frameArgs;
  const cells = new Map(
    comparison.cells.map((cell) => [
      allocationCellKey(cell.subAccountId, cell.financialProductId),
      cell,
    ]),
  );
  const accountTotals = new Map(
    comparison.accountTotals.map((total) => [total.subAccountId, total]),
  );
  const frame = buildAccountAllocationFrameSlots({
    ...frameArgs,
    accountColumnSpan: COMPARISON_COLUMNS_PER_ACCOUNT,
  });
  const cash = formatPercentValue(report.expectedCashPercentage);

  return {
    ...frame,
    subAccountSubHeadCells: () => (
      <>
        <TableHeadCell number>
          {t('components:accountAllocationGrid.comparison.proposedColumn')}
        </TableHeadCell>
        <TableHeadCell number>
          {t('components:accountAllocationGrid.comparison.currentColumn')}
        </TableHeadCell>
      </>
    ),
    // Cash isn't part of the matrix, so it's the same on both sides.
    subAccountSummaryCell: (subAccount, position) => (
      <>
        {frame.subAccountSummaryCell(subAccount, position)}
        {textCell(cash)}
      </>
    ),
    holdingCell: (security, subAccount) => {
      const cell = cells.get(allocationCellKey(subAccount.id, security.id));
      return (
        <>
          {proposedCell(
            cell,
            TESTID_ACCOUNT_ALLOCATION_CELL(subAccount.id, security.id),
            t,
          )}
          {textCell(
            percentOrDash(cell?.current),
            TESTID_ACCOUNT_ALLOCATION_CURRENT_CELL(subAccount.id, security.id),
          )}
        </>
      );
    },
    subAccountTotalCell: (subAccount) => {
      const total = accountTotals.get(subAccount.id);
      return (
        <>
          {textCell(
            percentOrDash(total?.proposed),
            TESTID_ACCOUNT_ALLOCATION_TOTAL_CELL(subAccount.id),
          )}
          {textCell(
            percentOrDash(total?.current),
            TESTID_ACCOUNT_ALLOCATION_CURRENT_TOTAL_CELL(subAccount.id),
          )}
        </>
      );
    },
  };
};
